import { useEffect, useState } from "react";
import { api, formatINR, downloadXlsx } from "@/lib/api";
import { Loader2, Plus, X, FileSpreadsheet } from "lucide-react";

export default function Loans() {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/loans");
    setItems(data); setLoading(false);
  };
  useEffect(() => { api.get("/employees").then((r) => setEmployees(r.data)); load(); }, []);

  return (
    <div className="space-y-6" data-testid="loans-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Salary Advances</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Loans & Advances</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadXlsx("/loans/export.xlsx", "loans_register.xlsx")}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium"
                  style={{ background: "#1B4332" }} data-testid="export-loans-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => setShow(true)} data-testid="new-loan-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> New Loan
          </button>
        </div>
      </header>

      {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="card-flat overflow-hidden p-0">
          <table className="w-full text-sm" data-testid="loans-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60">
              <th className="px-6 py-3">Employee</th><th>Type</th><th>Amount</th>
              <th>EMI</th><th>Tenure</th><th>Balance</th><th>Status</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="7" className="py-10 text-center text-stone-500">No loans yet</td></tr>}
              {items.map((l) => (
                <tr key={l.id} className="border-t border-stone-100">
                  <td className="px-6 py-3 font-medium">{l.employee_name}</td>
                  <td>{l.loan_type}</td>
                  <td>{formatINR(l.amount)}</td>
                  <td>{formatINR(l.emi)}</td>
                  <td>{l.tenure_months}m</td>
                  <td style={{ color: "var(--color-secondary)" }} className="font-semibold">{formatINR(l.balance)}</td>
                  <td><span className="status-pill" style={{ background: "var(--color-primary-light)", color: "var(--color-primary)" }}>{l.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {show && <LoanModal employees={employees} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function LoanModal({ employees, onClose, onSaved }) {
  const [form, setForm] = useState({
    employee_id: employees[0]?.id || "", loan_type: "Loan",
    amount: 100000, tenure_months: 12, emi: 0,
    start_date: new Date().toISOString().slice(0, 10), reason: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.post("/loans", { ...form, amount: Number(form.amount), tenure_months: Number(form.tenure_months), emi: Number(form.emi) || 0 });
    setBusy(false); onSaved();
  };
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 flex items-center justify-center p-4" data-testid="loan-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">New Loan / Advance</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label><div className="kpi-label mb-1.5">Employee</div>
            <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
            </select>
          </label>
          <label><div className="kpi-label mb-1.5">Type</div>
            <select value={form.loan_type} onChange={(e) => setForm({ ...form, loan_type: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
              <option>Loan</option><option>Salary Advance</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <In label="Amount ₹" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
            <In label="Tenure (months)" type="number" value={form.tenure_months} onChange={(v) => setForm({ ...form, tenure_months: v })} />
            <In label="EMI ₹ (optional)" type="number" value={form.emi} onChange={(v) => setForm({ ...form, emi: v })} />
            <In label="Start Date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
          </div>
          <label><div className="kpi-label mb-1.5">Reason</div>
            <textarea value={form.reason} rows="2" onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
          </label>
          <button type="submit" disabled={busy} data-testid="save-loan-button"
                  className="w-full py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            {busy ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function In({ label, type, value, onChange }) {
  return <label><div className="kpi-label mb-1.5">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
           className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
  </label>;
}
